import * as React from 'react'
import clsx from 'clsx'
import { FormControlProps } from '../FormControl'
import { SxProps } from '../sx'
import { useSelectAdvance, UseSelectAdvanceProps } from './useSelectAdvance'
import { Popper, PopperProps } from '../Popper'
import styles from './index.module.scss'
import Search from '../internal/svg-icons/Search'
import { OutlinedInput } from '../OutlinedInput'
import { PaperProps, Paper } from '../Paper'
import { List, ListProps } from '../List'
import { ListItemButtonBaseProps } from '../ListItemButton'
import { InputBaseProps } from '../InputBase'
import { Box } from '../Box'
import {
  RenderListOptionItemProps,
  RenderListOptionItemSkeletonProps,
  DefaultSkeletonListOptionItem,
  DefaultListOptionItemWrapper,
} from './cellTypes'
import { NoResultFoundOverlay, NoRowsOverlay, ErrorOverlay } from './overlay'
import Close from '../internal/svg-icons/Close'
import { IconButton } from '../IconButton'
import { sizeToFontSize } from '../utils/sizeToFontSize'
import { SelectAllBar } from './common'

export interface SelectAdvancePopperProps<Value>
  extends Omit<UseSelectAdvanceProps<Value>, 'onClose' | 'open'>,
    Omit<
      Partial<React.HTMLAttributes<HTMLInputElement>>,
      'defaultValue' | 'value' | 'size' | 'onChange'
    >,
    Pick<
      PopperProps,
      | 'anchorEl'
      | 'placement'
      | 'keepMounted'
      | 'open'
      | 'disablePortal'
      | 'popperRef'
    > {
  sx?: SxProps
  size?: FormControlProps['size']
  renderOption?: RenderListOptionItemProps<Value>['renderOption']
  renderListItem?: (props: RenderListOptionItemProps<Value>) => JSX.Element
  renderListItemSkeleton?: (
    props: RenderListOptionItemSkeletonProps
  ) => JSX.Element
  overlay?: 'noRows' | 'noResult' | 'error'
  slotProps?: {
    PaperProps?: PaperProps
    InputBaseProps?: InputBaseProps
    ListProps?: ListProps
    ListItemButtonProps?: ListItemButtonBaseProps
    errorOverlay?: any
    noResultsOverlay?: any
    noRowsOverlay?: any
  }
  slots?: {
    NoRowsOverlay?: React.JSXElementConstructor<any>
    NoResultOverlay?: React.JSXElementConstructor<any>
    ErrorOverlay?: React.JSXElementConstructor<any>
  }
  /**
   * Render the custom element at top of the dropdown
   */
  renderContentTop?: JSX.Element
  /**
   * Render the custom element at bottom of the dropdown
   */
  renderContentBottom?: JSX.Element
  /**
   * Hide and show - Selct All navbar
   * @default false
   */
  hideSelectNav?: boolean
  /**
   * Hide and show - search handler
   * @default false
   */
  hideSearch?: boolean
  /**
   * SelectAll - handler
   */
  onSelectAll?: (isAllSelected: boolean) => void
}

const DefaultListOptionItem = DefaultListOptionItemWrapper<any>()

export function SelectAdvancePopper<Value>(
  props: SelectAdvancePopperProps<Value>
) {
  const {
    id,
    anchorEl,
    placement,
    keepMounted,
    disablePortal,
    open,
    readOnly,
    disabled,
    size,
    renderOption,
    renderListItem: RenderListItem = DefaultListOptionItem,
    renderListItemSkeleton:
      RenderListItemSkeleton = DefaultSkeletonListOptionItem,
    slotProps,
    overlay,
    slots = {
      NoRowsOverlay: NoRowsOverlay,
      NoResultOverlay: NoResultFoundOverlay,
      ErrorOverlay: ErrorOverlay,
    },
    onFetchSubRows,
    renderContentTop,
    renderContentBottom,
    hideSelectNav = false,
    hideSearch = false,
    // onMouseLeave,
    onChange,
    multiple,
    onSelectAll,
    style,
    popperRef,
    ...advanceSelectHookProps
  } = props
  const {
    getInputProps,
    getPaperProps,
    getOptionProps,
    getListboxProps,
    tableInstance,
    virtualRow,
    isRowLoading,
    getClearAll,
    getIsAllSelected,
    getSelectAll,
    clearInputValue,
  } = useSelectAdvance({
    ...advanceSelectHookProps,
    readOnly,
    disabled,
    onChange,
    multiple,
    // @ts-ignore
    hasOriginalOnChange: typeof onChange === 'function',
  })

  // @ts-ignore
  const clientWidth = React.useMemo(() => anchorEl?.clientWidth, [anchorEl])

  return (
    <React.Fragment>
      {anchorEl ? (
        <Popper
          id={id!}
          anchorEl={anchorEl}
          open={open!}
          placement={placement}
          disablePortal={disablePortal}
          keepMounted={keepMounted}
          popperRef={popperRef}
          slotProps={{
            root: {
              classes: {
                root: 'devarsh',
              },
            },
          }}
          style={{
            width: '100%',
            maxWidth: clientWidth,
            ...style,
          }}
        >
          <Paper
            {...slotProps?.PaperProps}
            {...getPaperProps()}
            className={clsx(
              styles.menuWrapper,
              slotProps?.PaperProps?.className
            )}
            style={{ ...slotProps?.PaperProps?.style }}
          >
            <Box className={styles['renderContent--top']}>
              {renderContentTop && renderContentTop}
              {!hideSearch ? (
                <Box className={styles.selectSearch}>
                  <OutlinedInput
                    {...getInputProps()}
                    placeholder="Search"
                    size={size}
                    fullWidth
                    startAdornment={<Search fontSize={sizeToFontSize(size)} />}
                    endAdornment={
                      Boolean(getInputProps().value) === true ? (
                        <IconButton onClick={clearInputValue}>
                          <Close fontSize={sizeToFontSize(size)} />
                        </IconButton>
                      ) : null
                    }
                    {...slotProps?.InputBaseProps}
                  />
                </Box>
              ) : null}
              {!hideSelectNav && multiple ? (
                <SelectAllBar
                  getClearAll={getClearAll}
                  tableInstance={tableInstance}
                  getIsAllSelected={getIsAllSelected}
                  getSelectAll={getSelectAll}
                  selectAllHandler={onSelectAll}
                />
              ) : null}
            </Box>
            {overlay ? (
              <Box className={styles.menuOverlay}>
                {overlay === 'error'
                  ? React.createElement(
                      slots.ErrorOverlay!,
                      slotProps?.errorOverlay
                    )
                  : overlay === 'noResult'
                    ? React.createElement(
                        slots.NoResultOverlay!,
                        slotProps?.noResultsOverlay
                      )
                    : overlay === 'noRows'
                      ? React.createElement(
                          slots.NoRowsOverlay!,
                          slotProps?.noRowsOverlay
                        )
                      : null}
              </Box>
            ) : (
              <Box className={styles.menuList} {...getListboxProps()}>
                <List
                  {...slotProps?.ListProps}
                  style={{
                    height: virtualRow.getTotalSize(),
                    position: 'relative',
                  }}
                  size={size}
                >
                  {virtualRow.getVirtualItems().map(current => {
                    const row = tableInstance.getRowModel().rows[current.index]
                    const style = {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${current.start}px)`,
                    }
                    if (row) {
                      const props = getOptionProps(row, current)
                      const currentRowLoading = isRowLoading?.[row.id] ?? false
                      return (
                        <RenderListItem
                          {...props}
                          table={tableInstance}
                          key={props.key}
                          row={row}
                          renderOption={renderOption}
                          style={style}
                          virtualItem={current}
                          measureElement={virtualRow.measureElement}
                          size={size}
                          ListItemButtonProps={slotProps?.ListItemButtonProps}
                          currentRowLoading={currentRowLoading}
                          onFetchSubRows={onFetchSubRows}
                        />
                      )
                    } else {
                      return (
                        <RenderListItemSkeleton
                          virtualItem={current}
                          key={`skeleton-${current.key}`}
                          style={style}
                          measureElement={virtualRow.measureElement}
                          size={size}
                        />
                      )
                    }
                  })}
                </List>
              </Box>
            )}
          </Paper>
          {renderContentBottom && renderContentBottom}
        </Popper>
      ) : null}
    </React.Fragment>
  )
}
